import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { organizationApiClient } from '../services/organizationApi';
import { setActiveOrganizationId } from '../services/apiContext';
import { canEditBrief, canEdit, canManageUsers, canManageBilling, getRoleLevel, getUserRoleInOrgBySlug } from '../utils/permissions';

const OrganizationContext = createContext();

// Local storage key for persisting current org
const CURRENT_ORG_KEY = 'cornercast_current_org_slug';

/**
 * Hook to access organization context
 */
export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};

/**
 * Organization Provider Component
 * Manages current organization selection, permissions, and related data
 */
export const OrganizationProvider = ({ children }) => {
  const { user, organizations, hasOrganizations, isSingleOrg, loading: authLoading } = useAuth();

  // Current organization state
  const [currentOrg, setCurrentOrgState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Related data
  const [advertisers, setAdvertisers] = useState([]);
  const [advertisersLoading, setAdvertisersLoading] = useState(false);
  const [creditBalance, setCreditBalance] = useState(null);
  const [creditsLoading, setCreditsLoading] = useState(false);

  /**
   * Set current organization and persist to localStorage
   */
  const setCurrentOrg = useCallback((org) => {
    setCurrentOrgState(org);
    setActiveOrganizationId(org?.id || null);
    if (org?.slug) {
      localStorage.setItem(CURRENT_ORG_KEY, org.slug);
    } else {
      localStorage.removeItem(CURRENT_ORG_KEY);
    }
    // Clear related data when org changes
    setAdvertisers([]);
    setCreditBalance(null);
  }, []);

  /**
   * Switch to a different organization by slug
   */
  const switchOrg = useCallback((orgSlug) => {
    const org = organizations.find(o => o.slug === orgSlug);
    if (org) {
      setCurrentOrg(org);
      return true;
    }
    return false;
  }, [organizations, setCurrentOrg]);

  /**
   * Initialize current organization on mount or when organizations change
   */
  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!hasOrganizations) {
      setCurrentOrgState(null);
      setActiveOrganizationId(null);
      setIsLoading(false);
      return;
    }

    // Try to restore from localStorage
    const savedSlug = localStorage.getItem(CURRENT_ORG_KEY);
    const savedOrg = savedSlug ? organizations.find(o => o.slug === savedSlug) : null;

    if (savedOrg) {
      // Restore saved organization
      setCurrentOrgState(savedOrg);
      setActiveOrganizationId(savedOrg.id || null);
    } else if (isSingleOrg) {
      // Auto-select if single org (setCurrentOrg syncs module state)
      setCurrentOrg(organizations[0]);
    } else {
      // Multi-org, no saved preference - leave null for org picker
      setCurrentOrgState(null);
      setActiveOrganizationId(null);
    }

    setIsLoading(false);
  }, [authLoading, organizations, hasOrganizations, isSingleOrg, setCurrentOrg]);

  /**
   * Get user's role in current organization
   */
  const currentRole = useMemo(() => {
    if (!currentOrg) return null;
    return getUserRoleInOrgBySlug(organizations, currentOrg.slug);
  }, [currentOrg, organizations]);

  /**
   * Permission helpers based on current role
   */
  const permissions = useMemo(() => {
    return {
      canView: !!currentRole,
      canEditBrief: canEditBrief(currentRole),
      canEdit: canEdit(currentRole),
      canManageUsers: canManageUsers(currentRole),
      canManageBilling: canManageBilling(currentRole),
      level: getRoleLevel(currentRole),
    };
  }, [currentRole]);

  /**
   * Load advertisers for current organization
   */
  const loadAdvertisers = useCallback(async () => {
    if (!currentOrg?.id) return [];

    setAdvertisersLoading(true);
    try {
      const result = await organizationApiClient.listAdvertisers(currentOrg.id);
      setAdvertisers(result.advertisers || []);
      return result.advertisers || [];
    } catch (error) {
      console.error('Failed to load advertisers:', error);
      setAdvertisers([]);
      return [];
    } finally {
      setAdvertisersLoading(false);
    }
  }, [currentOrg?.id]);

  /**
   * Load credits for current organization
   */
  const loadCredits = useCallback(async () => {
    if (!currentOrg?.id) return null;

    setCreditsLoading(true);
    try {
      const result = await organizationApiClient.getCredits(currentOrg.id);
      setCreditBalance(result.balance);
      return result;
    } catch (error) {
      console.error('Failed to load credits:', error);
      setCreditBalance(null);
      return null;
    } finally {
      setCreditsLoading(false);
    }
  }, [currentOrg?.id]);

  /**
   * Create a new advertiser in current organization
   */
  const createAdvertiser = useCallback(async (data) => {
    if (!currentOrg?.id) {
      throw new Error('No organization selected');
    }
    const newAdvertiser = await organizationApiClient.createAdvertiser(currentOrg.id, data);
    // Refresh advertisers list
    await loadAdvertisers();
    return newAdvertiser;
  }, [currentOrg?.id, loadAdvertisers]);

  /**
   * Check if user has access to a specific organization slug
   */
  const hasAccessToOrg = useCallback((orgSlug) => {
    return organizations.some(o => o.slug === orgSlug);
  }, [organizations]);

  /**
   * Get organization by slug from user's organizations
   */
  const getOrgBySlug = useCallback((orgSlug) => {
    return organizations.find(o => o.slug === orgSlug) || null;
  }, [organizations]);

  const value = {
    // Current organization
    currentOrg,
    setCurrentOrg,
    switchOrg,

    // User's role and permissions in current org
    currentRole,
    ...permissions,

    // All organizations user has access to
    organizations,
    hasAccessToOrg,
    getOrgBySlug,

    // Advertisers in current org
    advertisers,
    advertisersLoading,
    loadAdvertisers,
    createAdvertiser,

    // Credits
    creditBalance,
    creditsLoading,
    loadCredits,

    // Loading state
    isLoading: isLoading || authLoading,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

export default OrganizationContext;
